// ============================================================================
// CASE PICKER MODAL — reusable case-search dialog for DIRECT allocations
// ============================================================================
// Filters litigation cases by type / borrower name / loan account / case code.
// Multi-select. Excludes already-picked cases.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useLitigationCases } from 'hooks/api/useLitigationCases';
import type {
  LitigationCaseListItem,
  LitigationCaseTypeCode,
} from 'api/types/litigationCase.types';

export interface PickedCase {
  id: string;
  caseCode: string;
  caseTypeCode: string;
  customerName?: string | null;
  accountNumber?: string | null;
  currentOutstanding?: number | null;
}

export interface CasePickerModalProps {
  isOpen: boolean;
  /** Cases the caller already has — they will not appear in the result list. */
  excludeCaseIds?: string[];
  /** Pre-filter the search by case type. */
  caseTypeFilter?: LitigationCaseTypeCode;
  onPick: (cases: PickedCase[]) => void;
  onClose: () => void;
}

const CASE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All types' },
  { value: 'CIVIL', label: 'Civil' },
  { value: 'ARBITRATION', label: 'Arbitration' },
  { value: 'SARFAESI', label: 'SARFAESI' },
  { value: 'CRIMINAL', label: 'Criminal' },
  { value: 'EXECUTION', label: 'Execution' },
  { value: 'CONSUMER', label: 'Consumer' },
  { value: 'REPO', label: 'Repossession' },
  { value: 'AUCTION', label: 'Auction' },
];

const fmtMoney = (n?: number | null) =>
  n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const CasePickerModal: React.FC<CasePickerModalProps> = ({
  isOpen, excludeCaseIds, caseTypeFilter, onPick, onClose,
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [caseType, setCaseType] = useState<string>(caseTypeFilter ?? '');
  const [selected, setSelected] = useState<Map<string, PickedCase>>(new Map());

  // Reset on open.
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setDebouncedSearch('');
      setCaseType(caseTypeFilter ?? '');
      setSelected(new Map());
    }
  }, [isOpen, caseTypeFilter]);

  // Debounce.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data, loading } = useLitigationCases(
    isOpen
      ? {
          page: 1,
          pageSize: 50,
          searchTerm: debouncedSearch || undefined,
          caseTypeCode: caseType ? (caseType as LitigationCaseTypeCode) : undefined,
          caseStatus: 'ALL',
        }
      : undefined,
  );

  const excludeSet = useMemo(() => new Set(excludeCaseIds ?? []), [excludeCaseIds]);
  const rows = useMemo<LitigationCaseListItem[]>(
    () => (data ?? []).filter(c => !excludeSet.has(c.id)),
    [data, excludeSet],
  );

  const toggle = useCallback((c: LitigationCaseListItem) => {
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(c.id)) {
        next.delete(c.id);
      } else {
        next.set(c.id, {
          id: c.id,
          caseCode: c.caseCode,
          caseTypeCode: c.caseTypeCode,
          customerName: c.customerName,
          accountNumber: c.accountNumber,
          currentOutstanding: c.currentOutstanding,
        });
      }
      return next;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (selected.size === 0) {
      notificationService.toast.warning('Pick at least one case.');
      return;
    }
    onPick(Array.from(selected.values()));
    onClose();
  }, [selected, onPick, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            <IconSystem.search size={18} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
            Pick litigation cases
          </h2>
          <button className="modal-close" onClick={onClose} title="Close">
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', overflow: 'hidden' }}>
          {/* Filters row */}
          <div className="form-grid-2" style={{ gap: '0.75rem' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Search (case code / customer / account)</label>
              <input
                type="text"
                className="form-input"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Type to search…"
                autoFocus
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <SyutSelect
                label="Litigation type"
                options={CASE_TYPE_OPTIONS}
                value={caseType}
                onChange={v => setCaseType((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>

          {/* Results — compact list, not SyutGrid (we want simple click-to-toggle rows) */}
          <div style={{
            border: '1px solid var(--form-border-color)',
            borderRadius: 6,
            overflow: 'auto',
            flex: 1,
            minHeight: 240,
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead style={{
                position: 'sticky', top: 0, zIndex: 1,
                background: 'var(--grid-header-bg, #dbeafe)',
              }}>
                <tr>
                  <th style={thStyle('48px')}> </th>
                  <th style={thStyle('150px')}>Case Code</th>
                  <th style={thStyle('110px')}>Type</th>
                  <th style={thStyle()}>Customer</th>
                  <th style={thStyle('160px')}>Account No</th>
                  <th style={{ ...thStyle('130px'), textAlign: 'right' }}>Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                      {debouncedSearch || caseType ? 'No matching cases.' : 'Type to search.'}
                    </td>
                  </tr>
                )}
                {!loading && rows.map(c => {
                  const checked = selected.has(c.id);
                  return (
                    <tr
                      key={c.id}
                      onClick={() => toggle(c)}
                      style={{
                        cursor: 'pointer',
                        background: checked ? 'rgba(37, 99, 235, 0.08)' : undefined,
                        borderBottom: '1px solid var(--border-color)',
                      }}
                    >
                      <td style={tdStyle()}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(c)}
                          onClick={e => e.stopPropagation()}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={tdStyle()}><strong>{c.caseCode}</strong></td>
                      <td style={tdStyle()}>{c.caseTypeDisplayName || c.caseTypeCode}</td>
                      <td style={tdStyle()}>{c.customerName ?? '—'}</td>
                      <td style={tdStyle()}>{c.accountNumber ?? '—'}</td>
                      <td style={{ ...tdStyle(), textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {fmtMoney(c.currentOutstanding)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            {selected.size} selected · {rows.length} shown
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleConfirm} disabled={selected.size === 0}>
            Add {selected.size > 0 ? `${selected.size} case${selected.size === 1 ? '' : 's'}` : ''}
          </button>
        </div>
      </div>
    </>
  );
};

const thStyle = (width?: string): React.CSSProperties => ({
  padding: '0.5rem 0.625rem',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '0.8rem',
  borderBottom: '2px solid var(--border-color)',
  whiteSpace: 'nowrap',
  width,
});

const tdStyle = (): React.CSSProperties => ({
  padding: '0.45rem 0.625rem',
  verticalAlign: 'middle',
});

export default CasePickerModal;
